import React, { Component, createRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import './Profile.css';

const fieldGroupStyle = {
    marginBottom: '8px',
};

class ProfileForm extends Component {
    constructor(props) {
        super(props);

        const { user } = props;

        this.state = {
            name: user?.name || '',
            email: user?.email || '',
            telefone: user?.telefone || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            photoFile: null,
            photoPreview: user?.profile_picture_url || null,
            hasPhoto: !!user?.has_photo,
            errors: {},
            successMessage: null,
            saving: false,
        };

        this.fileInputRef = createRef();
        this.toast = createRef();
    }

    handleAddPhotoClick = () => {
        this.fileInputRef.current && this.fileInputRef.current.click();
    };

    handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const previewUrl = URL.createObjectURL(file);
        this.setState({ photoFile: file, photoPreview: previewUrl, hasPhoto: true });

        // Reset input value so selecting the same file again triggers onChange
        e.target.value = '';
    };

    handleTelefoneChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
        let masked = digits;

        if (digits.length > 10) {
            // Celular: (XX) XXXXX-XXXX
            masked = digits.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3');
        } else if (digits.length > 6) {
            // Fixo em formação: (XX) XXXX-XXXX
            masked = digits.replace(/^(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3');
        } else if (digits.length > 2) {
            masked = digits.replace(/^(\d{2})(\d+)/, '($1) $2');
        } else if (digits.length > 0) {
            masked = digits.replace(/^(\d+)/, '($1');
        }

        this.setState({ telefone: masked });
    };

    handleRemovePhoto = async () => {
        const { routes, csrfToken } = this.props;

        try {
            await axios.delete(routes.removePhoto, {
                headers: {
                    'X-XSRF-TOKEN': csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            this.setState({ hasPhoto: false, photoFile: null, photoPreview: null });

            this.toast.current && this.toast.current.show({
                severity: 'success',
                summary: 'Foto removida',
                detail: 'Foto de perfil removida com sucesso.',
                life: 3000,
            });
        } catch (e) {
            console.error(e);
            this.toast.current && this.toast.current.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Não foi possível remover a foto.',
                life: 3000,
            });
        }
    };

    handleSubmit = async (e) => {
        e.preventDefault();
        const { routes, csrfToken } = this.props;
        const { name, email, telefone, currentPassword, newPassword, confirmPassword, photoFile } = this.state;

        this.setState({ saving: true, errors: {}, successMessage: null });

        const formData = new FormData();
        formData.append('name', name);
        formData.append('email', email);
        formData.append('telefone', telefone);

        if (currentPassword) formData.append('old_password', currentPassword);
        if (newPassword) formData.append('password', newPassword);
        if (confirmPassword) formData.append('password_confirmation', confirmPassword);
        if (photoFile) formData.append('photo', photoFile);

        // Laravel requires _method spoofing for PATCH via FormData
        formData.append('_method', 'PATCH');

        try {
            await axios.post(routes.profileUpdate, formData, {
                headers: {
                    'X-XSRF-TOKEN': csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'multipart/form-data',
                },
            });

            this.setState({
                saving: false,
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                photoFile: null,
            });

            this.toast.current && this.toast.current.show({
                severity: 'success',
                summary: 'Perfil atualizado',
                detail: 'Perfil atualizado com sucesso.',
                life: 3000,
            });
        } catch (err) {
            this.setState({ saving: false });

            if (err.response && err.response.status === 422) {
                this.setState({ errors: err.response.data.errors || {} });
            } else {
                this.toast.current && this.toast.current.show({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Ocorreu um erro ao salvar o perfil.',
                    life: 3000,
                });
            }
        }
    };

    renderFieldError(field) {
        const { errors } = this.state;
        const messages = errors[field];
        if (!messages || messages.length === 0) return null;
        return (
            <small style={{ color: '#c0392b', display: 'block', marginTop: '4px' }}>
                {Array.isArray(messages) ? messages[0] : messages}
            </small>
        );
    }

    render() {
        const { name, email, telefone, currentPassword, newPassword, confirmPassword, hasPhoto, photoPreview, saving } = this.state;

        return (
            <div className="profile-card">
                <Toast ref={this.toast} position="bottom-right" />

                {/* Avatar + photo buttons */}
                <label className="label-p" style={{ marginBottom: '8px' }}>Foto</label>
                <div className="profile-avatar-section">
                    <div className="profile-avatar">
                        {photoPreview ? (
                            <img src={photoPreview} alt="Avatar" />
                        ) : (
                            <img src="/img/user.png" alt="Avatar padrão" />
                        )}
                    </div>

                    {!hasPhoto ? (
                        <button
                            type="button"
                            onClick={this.handleAddPhotoClick}
                            className="btn btn-photo btn-photo--add"
                        >
                            ADICIONAR
                        </button>
                    ) : (
                        <button
                            type="button"
                            title="Remover Foto do Perfil"
                            onClick={this.handleRemovePhoto}
                            className="btn btn-photo btn-photo--remove"
                        >
                            REMOVER
                        </button>
                    )}

                    <input
                        type="file"
                        accept="image/*"
                        ref={this.fileInputRef}
                        style={{ display: 'none' }}
                        onChange={this.handleFileChange}
                    />
                </div>

                {/* Form fields */}
                <form onSubmit={this.handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={fieldGroupStyle}>
                        <label className="label-p">Nome completo</label>
                        <InputText
                            value={name}
                            onChange={(e) => this.setState({ name: e.target.value })}
                            style={{ width: '100%' }}
                        />
                        {this.renderFieldError('name')}
                    </div>

                    <div style={fieldGroupStyle}>
                        <label className="label-p">E-mail</label>
                        <InputText
                            value={email}
                            onChange={(e) => this.setState({ email: e.target.value })}
                            style={{ width: '100%' }}
                        />
                        {this.renderFieldError('email')}
                    </div>

                    <div style={fieldGroupStyle}>
                        <label className="label-p">Telefone</label>
                        <InputText
                            value={telefone}
                            onChange={this.handleTelefoneChange}
                            placeholder="(XX) XXXXX-XXXX"
                            style={{ width: '100%' }}
                        />
                        {this.renderFieldError('telefone')}
                    </div>

                    <div style={fieldGroupStyle}>
                        <label className="label-p">Senha atual</label>
                        <InputText
                            type="password"
                            value={currentPassword}
                            onChange={(e) => this.setState({ currentPassword: e.target.value })}
                            style={{ width: '100%' }}
                        />
                        {this.renderFieldError('old_password')}
                    </div>

                    <div style={fieldGroupStyle}>
                        <label className="label-p">Nova senha</label>
                        <InputText
                            type="password"
                            value={newPassword}
                            onChange={(e) => this.setState({ newPassword: e.target.value })}
                            style={{ width: '100%' }}
                        />
                        {this.renderFieldError('password')}
                    </div>

                    <div style={fieldGroupStyle}>
                        <label className="label-p">Confirme a nova senha</label>
                        <InputText
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => this.setState({ confirmPassword: e.target.value })}
                            style={{ width: '100%' }}
                        />
                        {this.renderFieldError('password_confirmation')}
                    </div>

                    <div className="profile-form-footer">
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn background-brain btn-save"
                        >
                            {saving ? 'SALVANDO...' : 'SALVAR'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }
}

export default ProfileForm;
